import { AfterViewInit, Component, DOCUMENT, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { FocusedFormComponent } from "../../component/wrapper/focused-form.component";
import { FirebaseError } from 'firebase/app';
import { AccountService } from '../../services/account.service';
import { getActiveSegmentBySubdomainPublic, GetActiveSegmentBySubdomainPublicData, GetActiveSegmentBySubdomainPublicRef } from '@dataconnect/account';
import { preRegRequirementsTests, TestReturn } from '../account/pre-reg-requirements-test';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getSubdomain } from '../../../../globalTypes/_helpers';
import { ActivatedRoute } from '@angular/router';
import { LimiterDirective } from '../../directive/limiter';

export type LoginMode = "login" | "signup" | "reset" | "verify";
export interface Rule {
  ruleDescription: string;
  requirementType: string;
  requirementValue: string;
  displayRuleDescription: boolean;
}

@Component({
  selector: 'msh-login',
  standalone: true,
  imports: [FormsModule, FocusedFormComponent, LimiterDirective],
  template: `
  <msh-focused-form (onSubmit)="onSubmit($event)" [isLoading]="isLoading" [cta]="cta || submitLabel[mode]">
    @if(isReauth && mode === 'login'){
      <div heading >Re-authentication Required</div>
    }@else{
      <div heading >{{submitLabel[mode]}}{{ segmentName ? ': ' + segmentName + ' Segment' : '' }}</div>
    }
    @if(mode === 'signup' && rules && rules.userMustMeet.length){
      <div class="text-sm m-4 text-copy/70 space-y-2">
        <div class="font-medium">Registration Requirements:</div>
        @for(rule of rules.userMustMeet; track rule){
          <div class="flex items-start gap-2">
            <div class="text-orange-400">&bull;</div>
            <div>{{rule.ruleDescription}}</div>
          </div>
        }
      </div>
    }
    <input
      type="email"
      placeholder="Email"
      [(ngModel)]="email"
      name="email"
      (keyup)="inputKeyUp($event)"
      [limiter]="150"
      required
    />
    @if(mode !== 'reset') {
      <input
        type="password"
        #passwordEl
        placeholder="Password"
        [(ngModel)]="password"
        name="password"
        (keyup)="inputKeyUp($event)"
        required
      />
    }
    <div footer class="text-center space-y-2 flex flex-col mt-8 gap-1">
      @if(errorMsg){
        <div class="text-red-500 bg-navy-50 px-2 py-6 rounded m-4 border border-red-500">{{errorMsg}}</div>
      }@else if(testFailureMessages.length){
        <div class=" bg-navy-50 px-2 py-6 rounded m-4 border border-red-500">
          <span class="text-red-500 ">Unfortunately you did not pass the segment requirements:</span>
          <div class="text-sm mt-2">
          @for(testFailureMessage of testFailureMessages; track testFailureMessages){
            <div>{{testFailureMessage}}</div>
          }
          </div>
        </div>
      }
        @if(mode === 'verify'){
          <div class="text-sm mb-4">{{instructions}}</div>
        }
      <a (click)="changeMode(opposite[mode])" class="text-sm cursor-pointer text-orange-400 hover:underline">{{ mode === 'login' ? 'Sign up' : 'Login'}}</a>
      <a (click)="changeMode('reset')" class="text-sm cursor-pointer text-orange-400 hover:underline">Forgot password?</a>
      @if(mode !== 'verify'){<a (click)="changeMode('verify')" class="text-sm cursor-pointer text-orange-400 hover:underline">Resend verification email</a>}
    </div> 
  </msh-focused-form>


  `,
})
export class LoginComponent implements AfterViewInit {
  @Input() mode:LoginMode = "login"
  @ViewChild('passwordEl') passwordEl!: ElementRef;
  segmentName = '';
  isReauth = false;
  instructions = '';
  MetAllVerifiedRequirements = false;
  testFailureMessages: string[] = [];
  opposite: {[mode in LoginMode]: LoginMode} = {login: 'signup', signup: 'login', reset: 'login', verify: 'login'}
  private document: Document = inject(DOCUMENT);
  isLoading = false;
  cta = '';
  submitLabel:{[mode in LoginMode]: string} = {
    login: "Login",
    signup: "Create Account",
    reset: "Send Reset Link",
    verify: "Login"
  } 
  rules: {userMustMeet: Rule[], verifiedUserMustMeet: Rule[]} = {userMustMeet: [], verifiedUserMustMeet: []};
  email = '';
  password = '';
  errorMsg =  '';

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
  ) {
    const subdomain = getSubdomain(this.document);
    console.log({subdomain})
    if(subdomain){
      this.applySubdomain(subdomain);
    }
  }

  ngAfterViewInit(): void {
    this.route.params.subscribe(params => {
      const email = params['email'].replace(/ /g, '+');
      if(email){
        setTimeout(() => {
        this.isReauth = true;
        this.email = email;
          console.log({passwordEl: this.passwordEl.nativeElement})
          this.passwordEl.nativeElement.focus();
        }, 800);
      }
    });
  }

  async applySubdomain(subdomain: string) {
      // query the database for a segment with that subdomain and if it exists,
      const segmentRef = await getActiveSegmentBySubdomainPublic({subdomain});
      const segment = segmentRef.data.segments[0];
      console.log({segmentRef})
      if(segment){
        this.segmentName = segment.name;
        // set the active segment in the account service so it can be used for things like pre-filling location data during onboarding
        segment.segmentRegistrationRequirements_on_segment.forEach(req => {
          const rule: Rule = {
            ruleDescription: req.ruleDescription,
            requirementType: req.requirementType,
            requirementValue: req.requirementValue,
            displayRuleDescription: req.displayRuleDescription,
          }
          if(req.userMustMeet) this.rules.userMustMeet.push(rule);
          if(req.verifiedUserMustMeet) this.rules.verifiedUserMustMeet.push(rule);
        });
        console.log({rules: this.rules})
      } else {
        console.warn("No segment found for subdomain", subdomain);
      }
  }

  changeMode(newMode: LoginMode) {
    this.mode = newMode;
    this.isLoading = false;
    this.errorMsg = '';
    this.testFailureMessages = [];
    if(newMode === 'verify'){
      this.instructions = 'If you haven\'t already verified your email, please try logging in and you will be prompted to resend the email. If you have not received a verification email, please check your spam folder.';
    }
  }

  inputKeyUp(event: KeyboardEvent){
    this.cta = '';
    if(this.mode === 'signup' && this.rules.verifiedUserMustMeet.length > 0){
      let verifiedRequirementsMet = true;
      for(let rule of this.rules.verifiedUserMustMeet){
        const key = rule.requirementValue as keyof typeof preRegRequirementsTests;
        if(preRegRequirementsTests[key] && this.email){
          const testResults: TestReturn = preRegRequirementsTests[key]({email: this.email}, this.snackBar);
          console.log({testResults})
          if(!testResults.success) {
            verifiedRequirementsMet = false;
            break;
          }
        }
      }
      if(verifiedRequirementsMet) this.cta = "✔ Create Verified Account";
    }
    if(event.key === 'Enter'){
      if(this.email && this.password){
        this.onSubmit(event)
      }
    }
  }

  async onSubmit(ev: Event) {
    console.log({ev: ev, mode: this.mode});
    this.errorMsg = '';
    this.isLoading = true;
    ev.preventDefault();
    switch(this.mode) {
      case 'verify':
      case 'login':
        try {
          const user = await this.authService.login(this.email, this.password);
          console.log({user})
          // await this.accountService.loadMyAccountInfo('login submit');
        } catch (error) {
          this.isLoading = false;
          if (error instanceof FirebaseError) {
            if(['auth/user-not-found','auth/wrong-password'].includes(error.code)){
              this.errorMsg = 'Incorrect user details';
              return  
            } else {
              this.errorMsg = error.message;
            }
          } else {
            this.errorMsg = 'Login failed';
          }
          console.error('Login failed', error);
        }
        break;
      case 'signup':
        // Perform signup
        this.testFailureMessages = [];
        for (let i = 0; i < this.rules.userMustMeet.length; i++) {
          const rule = this.rules.userMustMeet[i];
          const key = rule.requirementValue as keyof typeof preRegRequirementsTests;
          if(preRegRequirementsTests[key] && this.email){
            const testResults: TestReturn = preRegRequirementsTests[key]({email: this.email}, this.snackBar);
            console.log(testResults)
            if(!testResults.success) {
              this.testFailureMessages.push(testResults.failureMsg || "Failed requirement: "+rule.ruleDescription);
            }
          }
          
        }
        if(this.testFailureMessages.length > 0){
          this.isLoading = false;
          return;
        }
        try {
          const user = await this.authService.signUp(this.email, this.password);
          console.log({user})
        } catch (error) {
          this.isLoading = false;
          if (error instanceof FirebaseError) {
            if(['auth/email-already-in-use'].includes(error.code)){
              // TODO: check if the user is on another segment of the app and handle accordingly
              this.errorMsg = 'You have already signed up. Please login.';
              return  
            } else {
              this.errorMsg = error.message;
            }
          } else {
            this.errorMsg = 'Signup failed';
          }
          console.error({msg:'Signup failed', error});
        }
        break;
      case 'reset':
        try {
          await this.authService.sendPasswordResetEmail(this.email);
          this.snackBar.open('Password reset email sent', 'Close', { duration: 5000 });
        } catch (error) {
          this.isLoading = false;
          if (error instanceof FirebaseError) {
            this.errorMsg = error.message;
          } else {
            this.errorMsg = 'Password reset failed';
          }
          console.error('Password reset failed', error);
        }
        this.isLoading = false;
        break;
    } 
  }
}